import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import logoImage from "@/assets/logo-corre-digital.png";

const estadosBR = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA",
  "PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"
];

interface FormData {
  nome: string;
  email: string;
  senha: string;
  telefone: string;
  cidade: string;
  uf: string;
}

interface FormErrors {
  nome?: string;
  email?: string;
  senha?: string;
  telefone?: string;
  cidade?: string;
  uf?: string;
}

export default function AlunoCadastro() {
  const navigate = useNavigate();
  const next = new URLSearchParams(window.location.search).get("next");
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    nome: "",
    email: "",
    senha: "",
    telefone: "",
    cidade: "",
    uf: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Nome: exigir nome e sobrenome
    if (!formData.nome.trim()) {
      newErrors.nome = "Nome é obrigatório";
    } else if (formData.nome.trim().split(/\s+/).length < 2) {
      newErrors.nome = "Insira seu nome completo (nome e sobrenome)";
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = "E-mail é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "E-mail inválido";
    }

    // Senha
    if (!formData.senha.trim()) {
      newErrors.senha = "Senha é obrigatória";
    } else if (formData.senha.length < 6) {
      newErrors.senha = "Mínimo 6 caracteres";
    }

    // Telefone: validar formato completo (10 ou 11 dígitos)
    const phoneDigits = formData.telefone.replace(/\D/g, "");
    if (!phoneDigits) {
      newErrors.telefone = "Telefone é obrigatório";
    } else if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      newErrors.telefone = "Telefone inválido. Use o formato (11) 99999-9999";
    }

    // Cidade
    if (!formData.cidade.trim()) {
      newErrors.cidade = "Cidade é obrigatória";
    }

    // UF
    if (!formData.uf.trim()) {
      newErrors.uf = "UF é obrigatório";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "").slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Check if email is already used by a partner request or org
      const { data: partnerReq } = await supabase
        .from("partner_requests" as any)
        .select("id")
        .eq("contact_email", formData.email.trim())
        .in("status", ["pending", "approved"])
        .limit(1);

      if (partnerReq && (partnerReq as any[]).length > 0) {
        setErrors({ email: "Este e-mail já está vinculado a uma solicitação de parceria. Use outro e-mail." });
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.senha,
        options: {
          emailRedirectTo: `${window.location.origin}/aluno/onboarding${next ? `?next=${encodeURIComponent(next)}` : ""}`,
          data: {
            full_name: formData.nome,
            city: formData.cidade,
            state: formData.uf,
            phone: formData.telefone.replace(/\D/g, ""),
          },
        },
      });

      if (error) throw error;

      if (data.user && data.user.identities && data.user.identities.length === 0) {
        setErrors({ email: "Este e-mail já está cadastrado. Tente fazer login." });
        return;
      }

      navigate("/aluno/cadastro-confirmacao", { state: { email: formData.email } });
    } catch (err: any) {
      const msg = err.message || "Erro ao criar conta";
      if (msg.toLowerCase().includes("already") || msg.toLowerCase().includes("registered")) {
        setErrors({ email: "Este e-mail já está cadastrado. Tente fazer login." });
      } else {
        setErrors({ email: msg });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <div className="min-h-screen bg-background flex flex-col px-6 py-6">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center gap-4 mb-6"
      >
        <Button variant="ghost" size="icon" onClick={() => navigate(`/aluno/escolha${next ? `?next=${encodeURIComponent(next)}` : ""}`)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <img src={logoImage} alt="Corre Digital" className="h-7" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex-1"
      >
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-foreground">Criar meu perfil</h1>
          <p className="text-muted-foreground text-sm mt-1">Preencha seus dados para começar</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome completo</Label>
            <Input id="nome" placeholder="Seu nome" value={formData.nome}
              onChange={(e) => handleChange("nome", e.target.value)}
              className={errors.nome ? "border-destructive" : ""} />
            {errors.nome && <p className="text-xs text-destructive">{errors.nome}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" placeholder="seu@email.com" value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className={errors.email ? "border-destructive" : ""} />
            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="senha">Senha</Label>
            <Input id="senha" type="password" placeholder="Mínimo 6 caracteres" value={formData.senha}
              onChange={(e) => handleChange("senha", e.target.value)}
              className={errors.senha ? "border-destructive" : ""} />
            {errors.senha && <p className="text-xs text-destructive">{errors.senha}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="telefone">Telefone / WhatsApp</Label>
            <Input id="telefone" type="tel" placeholder="(11) 99999-9999"
              value={formatPhone(formData.telefone)}
              onChange={(e) => handleChange("telefone", e.target.value)}
              className={errors.telefone ? "border-destructive" : ""} />
            {errors.telefone && <p className="text-xs text-destructive">{errors.telefone}</p>}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="cidade">Cidade</Label>
              <Input id="cidade" placeholder="São Paulo" value={formData.cidade}
                onChange={(e) => handleChange("cidade", e.target.value)}
                className={errors.cidade ? "border-destructive" : ""} />
              {errors.cidade && <p className="text-xs text-destructive">{errors.cidade}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="uf">UF</Label>
              <Select value={formData.uf} onValueChange={(value) => handleChange("uf", value)}>
                <SelectTrigger className={errors.uf ? "border-destructive" : ""}>
                  <SelectValue placeholder="UF" />
                </SelectTrigger>
                <SelectContent>
                  {estadosBR.map((e) => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.uf && <p className="text-xs text-destructive">{errors.uf}</p>}
            </div>
          </div>

          <div className="pt-4">
            <Button type="submit" variant="hero" size="xl" className="w-full" disabled={isLoading}>
              {isLoading ? (<><Loader2 className="w-5 h-5 animate-spin" />Criando...</>) : "Concluir cadastro"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
